import EmptyState from "./EmptyState";
import { listObjectsInPath } from "../../../services/S3"
import { S3_objects } from "../../../interfaces/S3_objects";
import { _Object, CommonPrefix } from "@aws-sdk/client-s3";
import { Link, LoaderFunction, useLoaderData, useNavigate, useSearchParams, defer, Await } from "react-router-dom";
import { dateFormat, sizeFormat } from "../../../utils/format";
import { Column_Sort_Params } from "../../../interfaces/column_sort_params";
import { sortedDataBuilder, sortTermBuilder, sortedDirectoryBuilder } from "../../../utils/sorting";
import React from "react";

// Loads data from S3 bucket based on URL path. Returns an `S3_objects` object that can be used in the JSX output.
export const loader: LoaderFunction = async ({ params }): Promise<ReturnType<typeof defer>> => {
    const path: string = params['*'] ? params['*'] : "";

    // If a visitor tries to access a subdirectory directly and does not include a trailing forward slash, the page will not correctly render without appending the symbol.
    const validatedPath = path.slice(-1) != "/" && path !== "" ? `${path}/` : path;

    // See https://beta.reactrouter.com/en/main/guides/deferred for how deferred pages are loaded
    return defer({
        s3Objects: listObjectsInPath(validatedPath)
    });
}

// Handles logic for navigation back up to the parent directory. Takes the current directory as the `prefix` property.
const Navigation = (props: {prefix: string}) => {
    const navigate = useNavigate();
    const { prefix } = props;
    /* The prefix value will always include an ending "/" character, due to how the S3 API returns the prefix value. Thus,
       when the string is split, it'll always return an empty string element at the end of the array, and the -2 value is
       needed in the split. */
    const previousPrefix = prefix.split('/').slice(0, -2).join('/');
    /* The `replace` option will replace the current entry in the history stack instead of adding to the stack after navigation.
       This is required so that if a user uses the link twice, it will correct navigate to the parent directories instead of
       routing the user back and forth between the same pages. */
    return (<button onClick={() => navigate(previousPrefix, { replace: true })} className="parentDirectory" >Parent Directory</button>)
}

const Body = () => {
    const data = useLoaderData() as Record<"s3Objects", S3_objects>
        , [sortParams, setSortParams] = useSearchParams()
        , columnSortField = sortParams.get(Column_Sort_Params.C) as Column_Sort_Params
        , columnSortBy = sortParams.get(Column_Sort_Params.O) as Column_Sort_Params

    return (
        <div>
            <table className="tableWidth" >
                <thead>
                    <tr key={"Header"}>
                        <th className="firstCol"></th>
                        <th className="secondCol"><a onClick={() => setSortParams(sortTermBuilder(Column_Sort_Params.N, columnSortBy))}>Name</a></th>
                        <th className="thirdCol"><a onClick={() => setSortParams(sortTermBuilder(Column_Sort_Params.M, columnSortBy))}>Last modified</a></th>
                        <th className="fourthCol"><a onClick={() => setSortParams(sortTermBuilder(Column_Sort_Params.S, columnSortBy))}>Size</a></th>
                        <th></th>
                     </tr>
                    <tr key={"HeaderHR"}>
                        <td colSpan={ 4 }><hr /></td>
                    </tr>
                </thead>
                <React.Suspense fallback={<tbody><tr><td>Loading...</td></tr></tbody>}>
                    <Await resolve={data.s3Objects} errorElement={<tbody><tr><td>Error loading directory objects!</td></tr></tbody>}>
                        { (s3Objects) => {
                            const prefix = s3Objects.prefix;
                            const sortedFileData = sortedDataBuilder(s3Objects.objects, columnSortField, columnSortBy);
                            const sortedDirectoryData = sortedDirectoryBuilder(s3Objects.directories, columnSortField, columnSortBy);
                            
                            // Add fallback/404 if both are empty
                            if (sortedDirectoryData.length === 0 && sortedFileData.length === 0) {
                                return <EmptyState />;
                            }

                            return (
                                <tbody>
                                    <tr key={"BodyNav"}>
                                        {prefix === "" ?
                                            <>
                                                <td className="firstCol"></td>
                                            </>
                                            :
                                            <>
                                                <td className="firstCol"><img alt="back button icon" src="/img/icons/back.png" width="15px;"></img></td>
                                                <td className="secondCol"><Navigation prefix={`${prefix}`}/></td>
                                            </>
                                        }
                                    </tr>
                                    {
                                        sortedDirectoryData?.map((dir: CommonPrefix) => (
                                            <tr key={`BodyDir${dir.Prefix}`}>
                                                <td className="firstCol">
                                                    <img alt="folder icon image" src="/img/icons/folder.png" width="15px;"></img>
                                                </td>
                                                <td className="secondCol">
                                                    <Link to={dir.Prefix as string}>{dir.Prefix?.replace(prefix, '')}</Link>
                                                </td>
                                                <td className="thirdCol">-</td>
                                                <td className="fourthCol"> -</td>
                                            </tr>
                                        ))
                                    }
                                    {
                                        sortedFileData?.map( (obj: _Object) => (
                                            <tr key={`BodyObject${obj.Key}`}>
                                                <td className="firstCol">
                                                    { obj.Key?.includes("README") ? <img alt="hand pointing right icon" src="/img/icons/hand.right.gif" width="15px;"></img>
                                                        : obj.Key?.endsWith(".gz") ? <img alt="compressed document icon" src="/img/icons/compressed.gif" width="15px;"></img>
                                                        : <img alt="text document icon" src="/img/icons/text.png" width="15px;"></img> }
                                                </td>
                                                <td className="secondCol"><a href={ `${ window.location.origin }/${obj.Key as string}` }>{obj.Key?.replace(prefix, '')}</a></td>
                                                <td className="thirdCol">{dateFormat(obj.LastModified as Date)}</td>
                                                <td className="fourthCol">{sizeFormat(obj.Size as number)}</td>
                                            </tr>
                                        ))
                                    }
                                    <tr key={"BodyHR"}>
                                        <td colSpan={ 4 }><hr /></td>
                                    </tr>
                                </tbody>
                            )}
                        }
                    </Await>
                </React.Suspense>
           </table>
        </div>
    )
};

export default Body;